import React, { useMemo, useState } from 'react';
import Dialog from './TivigoDialog';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LiveTvRoundedIcon from '@mui/icons-material/LiveTvRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import UserPanelRemoveUserModal from './UserPanelRemoveUserModal';
import './userPanelManageModal.css';

const parseExpiry = (value) => {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric <= 0) return null;
  const date = Number.isFinite(numeric)
    ? new Date(numeric > 100000000000 ? numeric : numeric * 1000)
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getManagedAccount = (userInfo, usernameOverride) => {
  const info = userInfo?.user_info || {};
  const user = userInfo?.user || {};
  const expiry = parseExpiry(info.exp_date ?? user.exp_date ?? userInfo?.exp_date);
  const now = Date.now();
  const daysLeft = expiry ? Math.max(0, Math.ceil((expiry.getTime() - now) / 86400000)) : null;
  const status = String(info.status ?? user.status ?? userInfo?.status ?? '').toLowerCase();
  const isBanned = Number(info.admin_enabled ?? user.admin_enabled ?? userInfo?.admin_enabled ?? 1) === 0
    || status === 'banned';
  const isDisabled = Number(info.enabled ?? user.enabled ?? userInfo?.enabled ?? 1) === 0
    || status === 'disabled';
  const expired = Boolean(expiry && expiry.getTime() < now);

  return {
    username: usernameOverride || info.username || user.username || userInfo?.username || 'Tivigo kullanıcısı',
    expiry,
    daysLeft,
    expired,
    inactive: expired || isBanned || isDisabled,
    statusLabel: isBanned ? 'Yasaklı' : isDisabled ? 'Kısıtlı' : expired ? 'Süresi doldu' : 'Aktif',
  };
};

function UserPanelManageModal({
  open,
  onClose,
  userInfo,
  username,
  isOwnAccount = true,
  showRemoveAction = false,
  onOpenDuration,
  onOpenCategories,
  onOpenAccount,
  onOpenInstall,
  onOpenSecurity,
  onRemove,
}) {
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const account = useMemo(
    () => getManagedAccount(userInfo, username),
    [userInfo, username],
  );
  const canRemove = showRemoveAction && !isOwnAccount && typeof onRemove === 'function';

  const actions = [
    {
      id: 'duration',
      title: 'Süre Ekleyin',
      description: account.daysLeft == null
        ? 'Üyeliğiniz için uygun paketi seçin.'
        : account.daysLeft > 0
          ? `${account.daysLeft} gün içinde bitecek.`
          : 'Üyelik süreniz dolmuş.',
      icon: AccessTimeRoundedIcon,
      tone: 'orange',
      handler: onOpenDuration,
      availableWhenExpired: true,
    },
    {
      id: 'categories',
      title: 'Kategorileri Yönet',
      description: 'İçerik kategorilerini ekleyin veya kaldırın.',
      icon: LiveTvRoundedIcon,
      tone: 'violet',
      handler: onOpenCategories,
    },
    {
      id: 'account',
      title: 'Hesabı Düzenle',
      description: 'Kişisel, şifre ve iletişim bilgilerinizi yönetin.',
      icon: EditRoundedIcon,
      tone: 'blue',
      handler: onOpenAccount,
    },
    {
      id: 'install',
      title: 'Kurulum Yapın',
      description: 'Televizyon, telefon ve bilgisayar kurulumları.',
      icon: DevicesRoundedIcon,
      tone: 'green',
      handler: onOpenInstall,
    },
    {
      id: 'security',
      title: 'Güvenlik Kayıtları',
      description: 'Hesabınızdaki oturum ve güvenlik hareketleri.',
      icon: SecurityRoundedIcon,
      tone: 'gold',
      handler: onOpenSecurity,
    },
    ...(canRemove ? [{
      id: 'remove',
      title: 'Hesabı Kaldır',
      description: 'Bu alt kullanıcıyı hesabınızdan kaldırın.',
      icon: DeleteOutlineRoundedIcon,
      tone: 'neutral',
      dangerText: true,
      handler: () => setRemoveModalOpen(true),
      availableWhenExpired: true,
      keepManageOpen: true,
    }] : []),
  ];

  const closeManage = () => {
    setRemoveModalOpen(false);
    onClose?.();
  };

  const runAction = (action) => {
    const disabled = (!action.availableWhenExpired && account.expired)
      || typeof action.handler !== 'function';
    if (disabled) return;
    if (action.keepManageOpen) {
      action.handler();
      return;
    }
    closeManage();
    action.handler();
  };

  const confirmRemove = async () => {
    await onRemove?.();
    closeManage();
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth={false}
        keepMounted
        transitionDuration={0}
        disableEscapeKeyDown
        className="tvup-manage-dialog"
        aria-labelledby="tvup-manage-modal-title"
        aria-describedby="tvup-manage-modal-description"
        slotProps={{
          backdrop: { className: 'tvup-manage-backdrop' },
          paper: { id: 'tvup-manage-modal', className: 'tvup-manage-modal' },
        }}
      >
      <header className="tvup-manage-header">
        <div className="tvup-manage-title-icon" aria-hidden="true">
          <ManageAccountsRoundedIcon />
        </div>
        <div className="tvup-manage-title-copy">
          <h2 id="tvup-manage-modal-title">Üyeliği Yönet</h2>
          <p id="tvup-manage-modal-description">Hesabınızla ilgili tüm işlemler tek bir yerde.</p>
        </div>
        <button
          type="button"
          className="tvup-manage-close"
          aria-label="Üyelik yönetimi penceresini kapat"
          onClick={closeManage}
        >
          <CloseRoundedIcon />
        </button>
      </header>

      <div className="tvup-manage-body">
        <section className="tvup-manage-account" aria-label="Yönetilen hesap">
          <span className="tvup-manage-avatar" aria-hidden="true">
            {account.username.slice(0, 1).toLocaleUpperCase('tr-TR')}
          </span>
          <div>
            <small>YÖNETİLEN HESAP</small>
            <b>{account.username}</b>
          </div>
          <span className={`tvup-manage-status${account.inactive ? ' is-inactive' : ''}${account.expired ? ' is-expired' : ''}`}>
            {account.statusLabel}
          </span>
        </section>

        {account.expired && (
          <div className="tvup-manage-expired-note" role="status">
            Hesabınızın süresi dolduğu için süre ekleme dışındaki işlemler geçici olarak kapalıdır.
          </div>
        )}

        <section className="tvup-manage-actions" aria-label="Üyelik işlemleri">
          {actions.map((action) => {
            const Icon = action.icon;
            const disabled = (!action.availableWhenExpired && account.expired)
              || typeof action.handler !== 'function';

            return (
              <button
                key={action.id}
                type="button"
                className={`tvup-manage-action tone-${action.tone}${action.dangerText ? ' is-danger-text' : ''}`}
                disabled={disabled}
                aria-disabled={disabled}
                title={account.expired && !action.availableWhenExpired
                  ? 'Süre yükledikten sonra bu işlem aktif olacaktır.'
                  : undefined}
                onClick={() => runAction(action)}
              >
                <span className="tvup-manage-action-icon" aria-hidden="true"><Icon /></span>
                <span className="tvup-manage-action-copy">
                  <b>{action.title}</b>
                  <small>{action.description}</small>
                </span>
                <ArrowForwardRoundedIcon className="tvup-manage-action-arrow" aria-hidden="true" />
              </button>
            );
          })}
        </section>
      </div>
      </Dialog>
      <UserPanelRemoveUserModal
        open={open && removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
        username={account.username}
        onConfirm={confirmRemove}
      />
    </>
  );
}

export default UserPanelManageModal;
